/* A feed-evidenced row's reading (designs/kpi-feed-seam.md UI "Feed cell",
   KF13/KF16): line 1 the reading and its chip, line 2 the stamp and the
   button. The slot's evidence state is untouched by reachability (KF14) —
   red here marks the READING as not recorded. Pure: the owner does the
   POST /api/feeds/read, announces through #live, and passes the result back
   as `reading` / `inFlight` / `retriedAt` / `httpError`. `feedReadingLine`
   is the one-sentence form the certificate freezes (KF15, full date). */
import type { JSX } from "react";
import { metState, type FeedReading, type FeedTarget } from "../../../../shared/feeds";

const TARGET_SYMBOL: Record<FeedTarget["op"], string> = { ">=": "≥", "<=": "≤", ">": ">", "<": "<" };

const sameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const hm = (d: Date): string => d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
const monD = (d: Date): string => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

/* `HH:MM today` on the viewer's calendar day, else `Mon D, HH:MM` (KF16);
   the unreachable chip drops the word "today" — `unreachable since 09:12` */
export const timeLabel = (iso: string, now: Date = new Date(), today = " today"): string => {
  const d = new Date(iso);
  return sameDay(d, now) ? `${hm(d)}${today}` : `${monD(d)}, ${hm(d)}`;
};

/* the full `Mon D YYYY, HH:MM` every <time title> and every frozen text carries */
export const fullTime = (iso: string): string => {
  const d = new Date(iso);
  return `${monD(d)} ${d.getFullYear()}, ${hm(d)}`;
};

/* a % unit reads attached ("34% of manned"), any other spaced ("12 h") */
export const valueText = (value: number, unit: string | null): string =>
  unit === null || unit === "" ? String(value) : unit.startsWith("%") ? `${value}${unit}` : `${value} ${unit}`;

const targetText = (t: FeedTarget): string => `${TARGET_SYMBOL[t.op]} ${valueText(t.value, t.unit)}`;

const unitOf = (reading: FeedReading, target: FeedTarget | null): string | null =>
  (reading.state === "ok" ? reading.unit : null) ?? target?.unit ?? null;

const CHIP: Record<"met" | "not-met", string> = { met: "met", "not-met": "not met" };

export function feedReadingLine(reading: FeedReading, target: FeedTarget | null, measure: string): string {
  if (reading.state === "not-read-yet") return `${measure} not read yet`;
  if (reading.state === "unreachable") {
    const last = reading.lastOk
      ? `last read ${fullTime(reading.lastOk.readAt)} (${valueText(reading.lastOk.value, unitOf(reading, target))})`
      : "no successful read yet";
    return `${measure} unreachable since ${fullTime(reading.since)} · ${last}`;
  }
  const met = metState(reading, target);
  const verdict = met ? CHIP[met] : "no target";
  const vs = target ? ` vs ${targetText(target)}` : "";
  return `${measure} ${valueText(reading.value, unitOf(reading, target))}${vs} · ${verdict} · read ${fullTime(reading.readAt)}`;
}

/* what #live says when a read answers (kpi-feed-seam.md UI): "{row}: {chip},
   {value} vs {target} — read {time}", a fresh outage's since time, or "still
   unreachable, retried {time}" after a Retry that changed nothing */
export function feedAnnouncement(rowName: string, reading: FeedReading, target: FeedTarget | null, retriedAt: string | null, now: Date = new Date()): string {
  if (reading.state === "unreachable")
    return retriedAt
      ? `${rowName}: still unreachable, retried ${timeLabel(retriedAt, now)}`
      : `${rowName}: unreachable since ${timeLabel(reading.since, now)}`;
  if (reading.state === "not-read-yet") return `${rowName}: not read yet`;
  const met = metState(reading, target);
  const vs = target ? ` vs ${targetText(target)}` : "";
  return `${rowName}: ${met ? CHIP[met] : "no target"}, ${valueText(reading.value, unitOf(reading, target))}${vs} — read ${timeLabel(reading.readAt, now)}`;
}

function Time({ iso, now, bare }: { iso: string; now: Date; bare?: boolean }): JSX.Element {
  return (
    <time dateTime={iso} title={fullTime(iso)}>
      {timeLabel(iso, now, bare ? "" : undefined)}
    </time>
  );
}

export interface FeedRowValueProps {
  slotId: string;
  rowName: string;
  /* `{source label}` from FeedSourceInfo, e.g. "Power BI · Pilot KPIs" */
  sourceLabel: string;
  reading: FeedReading;
  /* the linked criterion's catalog target, never a product-side number (KF13) */
  target: FeedTarget | null;
  /* absent = no button (a frozen row) */
  onRead?: () => void;
  inFlight?: boolean;
  /* client clock at the answer of a Retry in this page session, or null */
  retriedAt?: string | null;
  /* KF16's HTTP-layer sentence, or null */
  httpError?: string | null;
  /* injectable for tests */
  now?: Date;
}

const BUTTON: Record<FeedReading["state"], string> = { ok: "Refresh", unreachable: "Retry", "not-read-yet": "Read" };

export function FeedRowValue({ slotId, rowName, sourceLabel, reading, target, onRead, inFlight, retriedAt, httpError, now }: FeedRowValueProps): JSX.Element {
  const clock = now ?? new Date();
  const met = reading.state === "ok" ? metState(reading, target) : null;
  return (
    <div className="feed-cell" data-slot={slotId}>
      <div className="feed-line">
        {reading.state === "ok" ? (
          <>
            <span className="feed-value">{valueText(reading.value, unitOf(reading, target))}</span>
            {target ? <span className="feed-target">vs {targetText(target)}</span> : null}
            {met ? <span className={`state ${met}`}>{CHIP[met]}</span> : <span className="state no-target">no target</span>}
          </>
        ) : reading.state === "unreachable" ? (
          <span className="state missing">
            unreachable since <Time iso={reading.since} now={clock} bare />
          </span>
        ) : (
          <span className="state missing">not read yet</span>
        )}
      </div>
      <div className="feed-line">
        <span className="stamp">
          {sourceLabel}
          {reading.state === "ok" ? (
            <>
              {" · read "}
              <Time iso={reading.readAt} now={clock} />
            </>
          ) : null}
          {reading.state === "unreachable" ? (
            reading.lastOk ? (
              <>
                {" · last read "}
                <Time iso={reading.lastOk.readAt} now={clock} /> ({valueText(reading.lastOk.value, unitOf(reading, target))})
              </>
            ) : (
              " · no successful read yet"
            )
          ) : null}
          {reading.state === "unreachable" && retriedAt ? (
            <>
              {" · retried "}
              <Time iso={retriedAt} now={clock} />
            </>
          ) : null}
        </span>
        {onRead ? (
          <button
            type="button"
            className="btn ghost small feed-read"
            id={`feed-read-${slotId}`}
            aria-label={`${inFlight ? "Reading" : BUTTON[reading.state]} ${rowName} feed`}
            aria-disabled={inFlight ? "true" : undefined}
            onClick={() => {
              if (!inFlight) onRead();
            }}
          >
            {inFlight ? "Reading…" : BUTTON[reading.state]}
          </button>
        ) : null}
      </div>
      {httpError ? (
        <p className="form-error" role="alert">
          {httpError}
        </p>
      ) : null}
    </div>
  );
}
